/**
 * The idea is that this project can be easily embed on different projects. This file handles the
 * actions needed to properly add this project on an iframe.
 */

import { state } from './state'
import { cleanString } from './utils'
import { inferMediaType } from './media'
import { LOCAL_DB, EMBED_SET_DATA, EMBED_DATA_READY, EMBED_DATA_ERROR } from './const'
import type { TravelLog, MediaItem, EmbedSetDataMessage } from './types'

function _normalizeExternalMedia(
  raw: Record<string, unknown>,
): MediaItem {
  const url = cleanString(raw.url)
  const blobId = cleanString(raw.blobId)
  const source: MediaItem['source'] =
    cleanString(raw.source) === LOCAL_DB && blobId
      ? LOCAL_DB
      : 'url'

  const mediaNormalized = {
    id: cleanString(raw.id) || crypto.randomUUID(),
    source,
    type: cleanString(raw.type) || inferMediaType(url),
  }

  if (source === LOCAL_DB)
    return Object.assign(mediaNormalized, { blobId, url: '' })

  return Object.assign(mediaNormalized, { blobId: '', url })
}

/**
 * We need to make sure that travel logs sent from an parent window
 * are on the format supported by this application. this functions is responsible for that
 * 
 * @param raw
 * @returns {TravelLog}
 */
function _normalizeExternalTravelLogs(raw: unknown): TravelLog {
  const travelLog = (raw ?? {}) as Record<string, unknown>

  const id = cleanString(travelLog.id) || crypto.randomUUID()
  const lat = Number(travelLog.lat)
  const lng = Number(travelLog.lng)

  /**
   * Make this validation before going through the media bc if this is invalid we do not even
   * worry about the media
   */
  if (!Number.isFinite(lat) || !Number.isFinite(lng))
    throw new Error('Invalid coordinates.')

  const media = (travelLog.media as Array<Record<string, unknown>>).map((m) => _normalizeExternalMedia(m))

  return {
    id,
    title:
      cleanString(travelLog.title) ||
      cleanString(travelLog.country) ||
      'Map item',
    note: cleanString(travelLog.note),
    country: cleanString(travelLog.country),
    lat,
    lng,
    visitedAt: cleanString(travelLog.visitedAt),
    media,
    createdAt: cleanString(travelLog.createdAt) || new Date().toISOString(),
  }
}

/**
 * Handle incoming `been-web:set-data` postMessages from the parent frame.
 * `onUpdate` is invoked after the state has been replaced (so callers can re-render).
 */
export async function handleEmbedMessage(
  event: MessageEvent,
  onUpdate: () => void,
): Promise<void> {
  const message = event.data as EmbedSetDataMessage

  if (!message || typeof message !== 'object') return
  if ((message).type !== EMBED_SET_DATA) return

  let responseMessage: Record<string, any> = { type: EMBED_DATA_READY, count: state.items.length }

  try {
    if (!message.payload || typeof message.payload !== 'object')
      throw new Error('Embed payload must be an object.')

    if (!message.payload.logs) throw new Error('Embed payload must include logs.')

    state.mediaUrls.forEach((url) => {
      if (url.startsWith('blob:')) URL.revokeObjectURL(url)
    })
    state.mediaUrls = new Map<string, string>()
    state.items = message.payload.logs.map(_normalizeExternalTravelLogs)
    state.selectedCountry = state.items[0]?.country ?? ''

    onUpdate()
  } catch (error) {
    responseMessage = { type: EMBED_DATA_ERROR, message: error instanceof Error ? error.message : 'Invalid embed data.' }
  }

  ; (event.source as Window | null)?.postMessage(responseMessage, event.origin)
}
